import { api } from '..';
import { TCortexOrganizationInsightsDTO } from '@/types/Cortex.types';
import { tagCortexInsightsVersion } from '@/utils/cortexInsights';
import { apiPaths } from '@/services/api/paths';

// Raw envelope as it comes off the wire — `insights` is whatever the backend stored
// (V1 legacy blob, V2 contract, or null while generation is PENDING/PROCESSING).
type RawCortexOrganizationInsightsDTO = Omit<TCortexOrganizationInsightsDTO, 'insightsVersion' | 'insights'> & {
  insights?: unknown;
};

/**
 * Triggers (or returns the cached / in-progress) Cortex AI organization insights.
 * Returns the full envelope with an `insightsVersion` discriminator attached — the
 * payload itself is not transformed, the UI branches to a dedicated V1 or V2
 * component based on the tag. `insights` is `null` (and `insightsVersion === 'NONE'`)
 * while async generation hasn't produced anything yet.
 */
export const postGenerateCortexOrganizationInsights = async (
  orgId: string,
): Promise<TCortexOrganizationInsightsDTO> => {
  const { data } = await api.post<RawCortexOrganizationInsightsDTO>(apiPaths.cortex.generate(orgId));

  const tagged = tagCortexInsightsVersion(data.insights);

  // Build a properly-typed discriminated DTO; one cast pins the union at the boundary.
  return {
    ...data,
    insightsVersion: tagged.version,
    insights: tagged.insights,
  } as TCortexOrganizationInsightsDTO;
};
